import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import {
  applicationsApplicationsListOptions,
  applicationsApplicationsPartialUpdate,
  applicationsApplicationsRepositoriesListOptions,
  applicationsCreateMutation
} from 'src/api/main'
import type { IconPickerValue } from 'src/components'
import { useAttachmentUpload } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { appIconPatchBody } from '../utils/appIcon'
import type { AppProductConfig } from '../utils/appProductConfig'
import { invalidateWorkspaceApplicationsListQueries } from '../utils/invalidateWorkspaceApplicationQueries'
import { mapApplicationToWorkspaceApp } from '../utils/mapApplicationToWorkspaceApp'
import { applicationsThirdPartyCreate } from '../utils/thirdPartyApi'

/** Larger page so Built-in / Custom tab filters see more API apps per workspace. */
const DEFAULT_PAGE_SIZE = 100

export type CreateWorkspaceApplicationInput = {
  name: string
  description?: string
  instance_type_id: number
  productConfig: AppProductConfig
  /** Codename of the role every workspace member should get the app with. */
  autoProvisionRoleCodename?: string
}

export type CreateCustomApplicationInput = {
  name: string
  description?: string
  appDomain?: string
  appUrl?: string
  icon?: IconPickerValue | null
}

type UseWorkspaceAppsListOptions = {
  /**
   * Include system applications — Core's own row, which carries a token and a
   * permission catalogue but is never launched. The API hides them by default,
   * so only the screens that bind credentials to them ask for them.
   */
  includeSystem?: boolean
}

export const useWorkspaceAppsList = (
  workspaceId: number,
  { includeSystem = false }: UseWorkspaceAppsListOptions = {}
) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { upload } = useAttachmentUpload()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const listEnabled = Number.isFinite(workspaceId) && workspaceId > 0

  const {
    data: listData,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...applicationsApplicationsListOptions({
      query: {
        organization: workspaceId,
        page,
        per_page: pageSize,
        // Sent only when asked for, so every other caller's request is unchanged.
        ...(includeSystem ? { include_system: true } : {})
      }
    }),
    enabled: listEnabled
  })

  const apiApps: WorkspaceApp[] =
    listData?.results?.map((a) =>
      mapApplicationToWorkspaceApp(a, workspaceId)
    ) ?? []

  const { mutateAsync: createMutate, isPending: isCreatingApp } = useMutation({
    ...applicationsCreateMutation(),
    onError: (err) => showApiErrors(err)
  })

  const createApp = async (
    values: CreateWorkspaceApplicationInput
  ): Promise<WorkspaceApp> => {
    const created = await createMutate({
      body: {
        organization_id: workspaceId,
        instance_type_id: values.instance_type_id,
        name: values.name,
        settings: {
          platform_core: values.productConfig
        },
        auto_provision_role_codename: values.autoProvisionRoleCodename
      }
    })

    const description = values.description?.trim()
    if (description) {
      await applicationsApplicationsPartialUpdate({
        path: { id: created.id },
        body: { description },
        throwOnError: true
      })
    }

    await invalidateWorkspaceApplicationsListQueries(queryClient, workspaceId)
    message.success(t('workspace.apps.messages.created'))

    const createdWithSettings = created as typeof created & {
      settings?: Record<string, unknown> | null
    }

    return mapApplicationToWorkspaceApp(
      description
        ? {
            ...createdWithSettings,
            description,
            settings: {
              ...(createdWithSettings.settings ?? {}),
              platform_core: values.productConfig
            }
          }
        : createdWithSettings,
      workspaceId
    )
  }

  const { mutateAsync: createCustomMutate, isPending: isCreatingCustomApp } =
    useMutation({
      mutationFn: applicationsThirdPartyCreate,
      onError: (err) => showApiErrors(err)
    })

  const createAppIconPatch = async (icon: IconPickerValue) => {
    try {
      return await appIconPatchBody(icon, (file) =>
        upload(file, workspaceId, { isPublic: true })
      )
    } catch (err) {
      showApiErrors(err)
      throw err
    }
  }

  const createCustomApp = async (
    values: CreateCustomApplicationInput
  ): Promise<WorkspaceApp> => {
    const iconPatch = values.icon ? await createAppIconPatch(values.icon) : null

    const created = await createCustomMutate({
      organization: workspaceId,
      application_name: values.name,
      application_description: values.description?.trim() || undefined,
      application_domain: values.appDomain?.trim() || undefined,
      application_url: values.appUrl?.trim() || undefined
    })

    if (iconPatch) {
      await applicationsApplicationsPartialUpdate({
        path: { id: created.id },
        body: iconPatch as never,
        throwOnError: true
      })
    }

    await invalidateWorkspaceApplicationsListQueries(queryClient, workspaceId)
    message.success(t('workspace.apps.messages.created'))

    return mapApplicationToWorkspaceApp(
      iconPatch ? { ...created, settings: iconPatch.settings } : created,
      workspaceId
    )
  }

  const refreshWorkspaceApps = () =>
    invalidateWorkspaceApplicationsListQueries(queryClient, workspaceId)

  return {
    /** Applications from the generated API (paginated). */
    apps: apiApps,
    listCount: listData?.count ?? 0,
    page,
    setPage,
    pageSize,
    setPageSize,
    isLoading,
    isError,
    refetch,
    createApp,
    isCreatingApp,
    createCustomApp,
    isCreatingCustomApp,
    refreshWorkspaceApps
  }
}

type RepositoriesListParams = {
  search?: string
  ordering?: string
  page?: number
  perPage?: number
}

export const useWorkspaceApplicationRepositories = (
  workspaceId: number,
  params: RepositoriesListParams = {}
) => {
  const enabled = Number.isFinite(workspaceId) && workspaceId > 0
  const trimmedSearch = params.search?.trim() || undefined

  return useQuery({
    ...applicationsApplicationsRepositoriesListOptions({
      // The schema types this filter as a string now.
      query: {
        organization: String(workspaceId),
        per_page: params.perPage ?? 100,
        page: params.page,
        ordering: params.ordering,
        search: trimmedSearch
      }
    }),
    enabled
  })
}

/**
 * Third-party custom applications (Custom apps tab) — fetched separately from
 * the main list so the backend returns only apps with
 * ``instance_type.type === 'third_party_custom'``.
 */
export const useWorkspaceThirdPartyCustomApps = (
  workspaceId: number,
  options?: { enabled?: boolean }
) => {
  const enabled =
    Number.isFinite(workspaceId) &&
    workspaceId > 0 &&
    (options?.enabled ?? true)

  const query = useQuery({
    ...applicationsApplicationsListOptions({
      query: {
        organization: workspaceId,
        instance_type__type: 'third_party_custom',
        per_page: 100
      }
    }),
    enabled
  })

  const apps: WorkspaceApp[] =
    query.data?.results?.map((a) =>
      mapApplicationToWorkspaceApp(a, workspaceId)
    ) ?? []

  return {
    apps,
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch
  }
}
